import React, { useState, useMemo } from "react";

const fmtEUR = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function RoiCalculator() {
  const [deals, setDeals] = useState(30);
  const [avgSize, setAvgSize] = useState(15000);
  const [slippers, setSlippers] = useState(2);
  const quarterlyLoss = useMemo(() => slippers * avgSize, [slippers, avgSize]);

  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20" data-testid="landing-roi-calculator">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
          <span className="text-emerald-500/70">/</span>what_s_it_worth
        </div>
        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
          What&rsquo;s a clearer Monday worth?
        </h2>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Slide the numbers to your reality. See what quietly slips today.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {/* Inputs */}
        <div className="space-y-6 rounded-2xl bg-white border border-slate-200 p-6">
          <Slider label="Deals in your pipeline right now" value={deals} min={5} max={200} step={1} onChange={setDeals} display={String(deals)} testid="roi-deals" />
          <Slider label="Average deal size" value={avgSize} min={1000} max={200000} step={500} onChange={setAvgSize} display={fmtEUR(avgSize)} testid="roi-avg" />
          <Slider label="Deals that quietly slip or die each quarter" value={slippers} min={0} max={20} step={1} onChange={setSlippers} display={String(slippers)} testid="roi-slippers" />
        </div>

        {/* Output */}
        <div className="rounded-2xl bg-slate-950 text-white p-8 flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-400/80">
            /quarterly_loss
          </div>
          <div className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tight text-emerald-400 number-tabular" data-testid="roi-output">
            ~{fmtEUR(quarterlyLoss)}
          </div>
          <div className="mt-3 text-slate-300 text-sm">
            per quarter in deals that slip through the cracks.
          </div>
          <div className="mt-6 pt-6 border-t border-slate-800 text-sm text-slate-300 leading-relaxed">
            Recrysta flags these before quarter-end — from{" "}
            <span className="text-white font-semibold">€99/month</span>.
          </div>
          <a href="/signup" className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 text-slate-950 px-5 py-3 text-sm font-bold hover:bg-emerald-400 transition-colors" data-testid="roi-cta">
            Start free 14-day trial →
          </a>
        </div>
      </div>
    </section>
  );
}

function Slider({ label, value, min, max, step, onChange, display, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="text-lg font-extrabold text-emerald-600 number-tabular">{display}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
      />
    </label>
  );
}
